<template>
    <div
        class="kanban-card"
        @click="onCardClick"
        @mouseover="$emit('hover', record._id)"
        @mouseleave="$emit('hover', null)"
        :class="{
            'hovered-card': isHovered,
            'selected-card': isSelected,
        }"
        :style="cardStyle()"
        :data-id="record._id"
        :data-index="recordIndex"
        :data-column-id="columnId"
        :data-column-index="columnIndex"
    >
        <!-- Selection Checkbox -->
        <div class="card-selection-checkbox" @click.stop="$emit('toggle-selection', record._id)">
            <i class="material-icons" v-if="isSelected">check_circle</i>
            <i class="material-icons" v-else>radio_button_unchecked</i>
        </div>

        <!-- İçerik Alanları -->
        <div v-for="(fld, idx) in cardFields" :key="idx" class="kanban-card-field">
            <!-- Alan Tipine Göre İkon -->
            <i class="material-icons" v-if="getFieldObject(fld)">
                {{ getFieldIcon(getFieldObject(fld)) }}
            </i>
            <label>{{ getFieldName(fld) }}:</label>

            <template v-if="getFieldObject(fld) && getFieldObject(fld).fieldType === 'linked-record'">
                <div class="linked-record-cell" @click.stop="viewLinkedRecord(record.data[fld], fld)">
                    <i class="material-icons">switch_access_shortcut</i>
                    <span class="linked-record-label">{{
                        getLinkedRecordLabel(record.data[fld], getFieldObject(fld))
                    }}</span>
                </div>
            </template>

            <template v-else-if="getFieldObject(fld) && getFieldObject(fld).fieldType === 'contact'">
                <div class="linked-record-cell" @click.stop="viewContact(record.data[fld])">
                    <PlatformIconViewer v-if="record.data[fld]" :platform="record.data[fld].platform" />
                    <span class="linked-record-label">{{ $_.get(record.data[fld], "fullname", "İsimsiz Kişi") }}</span>
                </div>
            </template>
            <template v-else-if="getFieldObject(fld) && getFieldObject(fld).fieldType === 'user'">
                <div class="linked-record-cell">
                    <AgentAvatar v-if="getUserInfo(record.data[fld])" :userID="record.data[fld]" :size="24" />
                    <span class="linked-record-label">{{ getUserLabel(record.data[fld]) }}</span>
                </div>
            </template>

            <template v-else-if="getFieldObject(fld) && getFieldObject(fld).fieldType === 'task'">
                <div class="linked-record-cell" @click.stop="viewTask(record.data[fld])">
                    <FieldIcon type="task" :size="16" :default-icon="getFieldObject(fld).icon" />
                    <span class="linked-record-label">{{ getTaskLabel(record.data[fld]) }}</span>
                </div>
            </template>

            <!-- Veriyi Formatlama -->
            <template v-else-if="getFieldObject(fld) && getFieldObject(fld).fieldType !== 'dropdown'">
                <span>
                    {{ getFormattedValue(getFieldObject(fld), record.data[fld]) }}
                </span>
            </template>
            <!-- Dropdown Alanı İçin Renkli Chip'ler -->
            <template v-else-if="getFieldObject(fld) && getFieldObject(fld).fieldType === 'dropdown'">
                <div class="selected-options">
                    <div
                        v-for="optionVal in getArrayValue(record.data[fld])"
                        :key="optionVal"
                        class="option-chip"
                        :style="{ backgroundColor: getDropdownColor(optionVal, getFieldObject(fld)) }"
                    >
                        {{ optionVal }}
                    </div>
                </div>
            </template>
            <!-- Linked Record -->

            <!-- Tanımsız Alan -->
            <template v-else>
                <span>{{ record.data[fld] }}</span>
            </template>
        </div>
    </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapState } from "vuex";
import PlatformIconViewer from "../../../../../components/common/PlatformIconViewer.vue";
import AgentAvatar from "../../../../../components/common/AgentAvatar.vue";
import FieldIcon from "../../FieldIcon.vue";

export default {
    name: "KanbanRecordCard",
    props: {
        record: {
            type: Object,
            required: true,
        },
        recordIndex: {
            type: Number,
            required: true,
        },
        columnId: {
            type: String,
            required: true,
        },
        columnIndex: {
            type: Number,
            required: true,
        },
        isSelected: {
            type: Boolean,
            default: false,
        },
        isHovered: {
            type: Boolean,
            default: false,
        },
        cardFields: {
            type: Array,
            required: true,
        },
        table: {
            type: Object,
            required: true,
        },
        getRecordColor: {
            type: Function,
            required: true,
        },
    },
    computed: {
        ...mapState({
            tables: (state) => state.table.tables,
        }),
    },
    methods: {
        ...mapActions({
            addNewWindow: "app/addNewWindow",
        }),
        cardStyle() {
            const conditionalColor = this.getRecordColor(this.record);

            return conditionalColor
                ? {
                      backgroundColor: conditionalColor,
                      borderColor: this.adjustColorBrightness(conditionalColor, -10),
                  }
                : {};
        },
        onCardClick() {
            this.$emit("card-click", this.record);
        },
        getFieldName(fieldId) {
            const field = this.table.fields.find((f) => f._id === fieldId);
            return field ? field.name : fieldId;
        },
        getFieldObject(fieldId) {
            const field = this.table.fields.find((f) => f._id === fieldId);

            return field || null;
        },
        getFieldIcon(field) {
            const map = {
                text: "text_fields",
                "long-text": "subject",
                number: "looks_one",
                currency: "attach_money",
                datetime: "event",
                dropdown: "arrow_drop_down",
                checkbox: "check_box",
                "linked-record": "switch_access_shortcut",
                contact: "person",
                ticket: "confirmation_number",
                task: "assignment",
                event: "event",
                user: "person",
                url: "link",
                email: "email",
                phone: "phone",
                json: "code",
                file: "attachment",
                image: "image",
                video: "videocam",
                audio: "audiotrack",
                color: "palette",
                "automation-button": "smart_button",
            };
            return map[field.fieldType] || "info";
        },
        getFormattedValue(field, value) {
            if (field.fieldType === "datetime" && field.options && field.options.datetime) {
                const timeCfg = field.options.datetime;
                let formatStr = timeCfg.format || "YYYY-MM-DD";
                if (timeCfg.includeTime && timeCfg.timeFormat) {
                    formatStr = formatStr + " " + timeCfg.timeFormat;
                }
                return value ? moment(value).format(formatStr) : "";
            }

            if (field.fieldType === "number" && field.options?.number?.decimalFormat) {
                const decimalFormat = field.options.number.decimalFormat;
                const floatVal = parseFloat(value);
                return isNaN(floatVal) ? value : floatVal.toFixed(decimalFormat.length);
            }

            if (field.fieldType === "currency" && field.options?.currency) {
                const curSymbol = field.options.currency.symbol || "$";
                const decimalPlace = field.options.currency.decimalFormat || "00";
                const floatValCur = parseFloat(value);
                if (isNaN(floatValCur)) return value;
                const curValue = floatValCur.toFixed(decimalPlace.length);
                return curSymbol + curValue;
            }

            if (field.fieldType === "linked-record") {
                return this.getLinkedRecordLabel(value, field);
            }

            return value;
        },
        getArrayValue(value) {
            if (!value) return [];
            return Array.isArray(value) ? value : [value];
        },
        getDropdownColor(optionVal, field) {
            if (!field.options?.dropdown) {
                return "rgb(210, 216, 243)";
            }

            const dropdown = field.options.dropdown;
            if (dropdown.mode === "dynamic") {
                return this.generateColorFromText(optionVal);
            }

            const option = (dropdown.options || []).find((opt) => opt.value === optionVal);
            return option?.color || "rgb(210, 216, 243)";
        },
        generateColorFromText(text) {
            let hash = 0;
            for (let i = 0; i < text.length; i++) {
                hash = text.charCodeAt(i) + ((hash << 5) - hash);
            }
            const hue = Math.abs(hash) % 360;
            const saturation = 25 + (Math.abs(hash) % 15);
            const lightness = 85 + (Math.abs(hash) % 10);
            return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
        },
        adjustColorBrightness(color, percent) {
            const num = parseInt(color.replace("#", ""), 16),
                amt = Math.round(2.55 * percent),
                R = (num >> 16) + amt,
                G = ((num >> 8) & 0x00ff) + amt,
                B = (num & 0x0000ff) + amt;
            return (
                "#" +
                (
                    0x1000000 +
                    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
                    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
                    (B < 255 ? (B < 1 ? 0 : B) : 255)
                )
                    .toString(16)
                    .slice(1)
            );
        },
        getLinkedRecordLabel(linkedRecord, field) {
            const remoteTable = this.tables.find((t) => t._id === field.options.linkedRecord.table);

            if (!linkedRecord) return "Bağlı Kayıt";

            const labelField = field.options.linkedRecord.labelField;
            const remoteField = remoteTable.fields.find((f) => f._id === labelField);
            if (remoteField.fieldType === "contact") {
                const contact = _.get(linkedRecord, `data.${labelField}`, undefined);
                return contact ? contact.fullname : "İsimsiz Kişi";
            } else if (remoteField.fieldType === "user") {
                const user = _.get(linkedRecord, `data.${labelField}`, undefined);
                return user ? user.name : "İsimsiz Kullanıcı";
            } else {
                return _.get(linkedRecord, `data.${labelField}`, "Bağlı Kayıt");
            }
        },
        viewLinkedRecord(linkedRecord, fieldId) {
            console.log("[KanbanRecordCard] viewLinkedRecord => ", {
                linkedRecord,
                fieldId,
            });
            const field = this.getFieldObject(fieldId);
            const table = this.tables.find((t) => t._id === field.options.linkedRecord.table);

            if (!linkedRecord) return;

            const title = `${table.name} - (${this.getLinkedRecordLabel(linkedRecord, field)})`;
            console.log("[KanbanRecordCard] viewLinkedRecord => " + title);
            this.addNewWindow({
                id: linkedRecord._id,
                type: "table-record-viewer",
                title,
                payload: {
                    tableId: table._id,
                    recordId: linkedRecord._id,
                },
                multiple: true,
                autoClose: false,
            });
        },

        viewContact(contact) {
            if (!contact) return;

            this.addNewWindow({
                type: "show-visitor",
                title: contact.fullname,
                payload: {
                    visitorId: contact._id,
                },
                footer: false,
                autoClose: false,
            });
        },
        getUserInfo(userId) {
            if (!userId) return null;
            return this.$store.state.user.users.data.find((user) => user._id === userId);
        },
        getUserLabel(record) {
            const user = typeof record === "string" ? this.getUserInfo(record) : record;
            return user ? user.name : "İsimsiz Kullanıcı";
        },
        getTaskInfo(taskId) {
            if (!taskId) return null;
            return this.$store.state.tasks.tasks.find((task) => task._id === taskId);
        },
        getTaskLabel(record) {
            return record ? record.title : "Görev Bulunamadı";
        },
        viewTask(record) {
            if (!record) return;

            this.addNewWindow({
                type: "editor-task",
                title: record.title,
                payload: {
                    taskId: record._id,
                },
                autoClose: false,
            });
        },
    },
    components: {
        PlatformIconViewer,
        AgentAvatar,
        FieldIcon,
    },
};
</script>

<style scoped lang="scss">
.kanban-card {
    background-color: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    padding: 10px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease;
    cursor: pointer;
    position: relative;
    margin-bottom: 8px;

    &:last-child {
        margin-bottom: 0;
    }

    &.hovered-card {
        filter: brightness(0.95);
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
    }

    &.selected-card {
        border-color: #1890ff !important;
    }

    .card-selection-checkbox {
        position: absolute;
        top: 8px;
        left: 8px;
        z-index: 1;
        cursor: pointer;

        .material-icons {
            font-size: 20px;
            transition: all 0.3s ease;

            &:first-child {
                color: #1890ff;
            }

            &:last-child {
                color: rgba(0, 0, 0, 0.25);

                &:hover {
                    color: #1890ff;
                }
            }
        }
    }

    .kanban-card-field {
        margin-bottom: 6px;
        display: flex;
        align-items: center;
        padding-left: 28px; // Checkbox için boşluk

        &:last-child {
            margin-bottom: 0;
        }

        i {
            margin-right: 6px;
            font-size: 16px;
            color: rgba(0, 0, 0, 0.45);
        }

        label {
            font-weight: 600;
            margin-right: 6px;
            margin-bottom: 0;
            color: rgba(0, 0, 0, 0.85);
            font-size: 0.9em;
        }

        span {
            color: rgba(0, 0, 0, 0.65);
        }

        .selected-options {
            display: flex;
            flex-wrap: wrap;
            gap: 4px;

            .option-chip {
                display: inline-block;
                padding: 2px 8px;
                border-radius: 12px;
                font-size: 0.85em;
                color: rgba(0, 0, 0, 0.65);
                transition: all 0.3s ease;

                &:hover {
                    opacity: 0.9;
                }
            }
        }
    }

    .linked-record-cell {
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        transition: background-color 0.2s ease;

        &:hover {
            background-color: #c0e0f5;

            .linked-record-label {
                text-decoration: underline;
            }
        }

        .linked-record-label {
            font-weight: 500;
        }
    }
}
</style>
